/**
 * Mobile Money Payment Page
 * Redirects to Mwetana Pay for mobile money payments
 */

import React, { useEffect, useState } from "react";
import { usePageTitle } from "../contexts/PageTitleContext";
import { paymentService } from "../services/api/payment.service";
import type { Currency } from "../types/payment.types";
import BusyOverlay from "../components/common/BusyOverlay";
import {
  ApplicationPermissionId,
  HasPermission,
  ModuleId,
} from "../utils/permissions";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showErrorToast } from "../utils/toast";
import { GENERIC_ERROR_MESSAGE } from "../utils/constants";
import "../styles/pages/Users.css";

export const MobileMoneyPayment: React.FC = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [loading, setLoading] = useState(true);

  // Payment form state
  const [certificateApplicationId, setCertificateApplicationId] =
    useState<number>(0);
  const [amount, setAmount] = useState<string>("");
  const [currencyId, setCurrencyId] = useState<number>(0);
  const [error, setError] = useState("");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);

    const handleResize = () => {
      const isDesktopView = window.matchMedia("(min-width: 881px)").matches;
      setSidebarOpen(isDesktopView);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle(
      "Mobile Money Payment",
      "Process mobile money payments via Mwetana Pay",
    );
    fetchData();
  }, [setPageTitle]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const currenciesData = await paymentService.getCurrencies();
      setCurrencies(currenciesData);
      setError("");
    } catch (err) {
      showErrorToast(GENERIC_ERROR_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (certificateApplicationId <= 0) {
      setError("Please enter a valid certificate application ID");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }
    if (!currencyId) {
      setError("Please select a currency");
      return;
    }

    // Get currency code for the URL
    const selectedCurrency = currencies.find((c) => c.id === currencyId);
    const currencyCode = selectedCurrency?.code || selectedCurrency?.name || "";

    // Build redirect URL with parameters
    const baseUrl = "https://www.mwetanapay.net";
    const params = new URLSearchParams({
      amount: amount,
      currency: currencyCode,
      applicationId: certificateApplicationId.toString(),
      reference: `CERT-${certificateApplicationId}-${Date.now()}`,
    });

    const redirectUrl = `${baseUrl}?${params.toString()}`;

    // Redirect to Mwetana Pay
    window.location.href = redirectUrl;
  };

  const canCreate = HasPermission(
    ModuleId.Payments,
    ApplicationPermissionId.Create,
  );

  if (!canCreate) {
    return (
      <>
        <Topbar
          sidebarOpen={sidebarOpen}
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        />
        <Sidebar isOpen={sidebarOpen} />
        <div className="main-content">
          <div className="users-container">
            <div className="alert alert-danger">
              You do not have permission to process mobile money payments.
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Topbar
        sidebarOpen={sidebarOpen}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
      />
      <Sidebar isOpen={sidebarOpen} />
      <div className="main-content">
        <div className="users-container">
          <div className="users-header">
            <div className="users-header-top">
              <h1 className="users-title">Mobile Money Payment</h1>
              <p className="users-subtitle">
                Process mobile money payments via Mwetana Pay
              </p>
            </div>
          </div>

          <div
            style={{
              maxWidth: "600px",
              margin: "2rem auto",
              padding: "2rem",
              background: "#fff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          >
            <form onSubmit={handleSubmit}>
              {error && (
                <div
                  className="alert alert-danger"
                  style={{ marginBottom: "1.5rem" }}
                >
                  {error}
                </div>
              )}

              <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label htmlFor="certificateApplicationId">
                  Certificate Application ID *
                </label>
                <input
                  id="certificateApplicationId"
                  type="number"
                  value={certificateApplicationId || ""}
                  onChange={(e) =>
                    setCertificateApplicationId(parseInt(e.target.value) || 0)
                  }
                  required
                  min="1"
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label htmlFor="amount">Amount *</label>
                <input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  min="0.01"
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                />
              </div>

              <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label htmlFor="currencyId">Currency *</label>
                <select
                  id="currencyId"
                  value={currencyId || ""}
                  onChange={(e) => setCurrencyId(parseInt(e.target.value))}
                  required
                  style={{
                    width: "100%",
                    padding: "0.5rem",
                    border: "1px solid #dee2e6",
                    borderRadius: "4px",
                  }}
                >
                  <option value="">Select Currency</option>
                  {currencies.map((currency) => (
                    <option key={currency.id} value={currency.id}>
                      {currency.code || currency.name}
                    </option>
                  ))}
                </select>
              </div>

              <div
                className="alert"
                style={{
                  background: "#d1ecf1",
                  border: "1px solid #bee5eb",
                  borderRadius: "4px",
                  padding: "1rem",
                  marginBottom: "1.5rem",
                  color: "#0c5460",
                }}
              >
                <div style={{ display: "flex", alignItems: "start" }}>
                  <span style={{ fontSize: "1.5rem", marginRight: "0.75rem" }}>
                    📱
                  </span>
                  <div>
                    <strong>Mobile Money Payment Process:</strong>
                    <p style={{ marginBottom: 0, marginTop: "0.5rem" }}>
                      You will be redirected to Mwetana Pay to complete your
                      mobile money payment securely. After successful payment,
                      you will be redirected back to this application.
                    </p>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="users-btn users-btn-primary"
                disabled={loading}
                style={{ width: "100%" }}
              >
                Proceed to Mwetana Pay
              </button>
            </form>
          </div>

          <div
            style={{
              maxWidth: "600px",
              margin: "2rem auto",
              padding: "1.5rem",
              background: "#f8f9fa",
              borderRadius: "8px",
              border: "1px solid #dee2e6",
            }}
          >
            <h3 style={{ marginTop: 0, fontSize: "1.125rem" }}>
              Supported Mobile Money Providers
            </h3>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              <div
                style={{
                  padding: "0.75rem",
                  background: "#fff",
                  borderRadius: "4px",
                  textAlign: "center",
                  border: "1px solid #dee2e6",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                  📱
                </div>
                <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  MTN Mobile Money
                </div>
              </div>
              <div
                style={{
                  padding: "0.75rem",
                  background: "#fff",
                  borderRadius: "4px",
                  textAlign: "center",
                  border: "1px solid #dee2e6",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                  📱
                </div>
                <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  Orange Money
                </div>
              </div>
              <div
                style={{
                  padding: "0.75rem",
                  background: "#fff",
                  borderRadius: "4px",
                  textAlign: "center",
                  border: "1px solid #dee2e6",
                }}
              >
                <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                  📱
                </div>
                <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>
                  Lonestar Cell MTN
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      <BusyOverlay isVisible={loading} />
    </>
  );
};

export default MobileMoneyPayment;
